import React from "react";
import { toast } from "sonner";
import { Save, PlugZap, Loader2, Check, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, SectionCard, StatusBadge, CardSkeleton } from "@/admin/ui";

function StatusRow({ label, ok }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/8 last:border-b-0">
      <div className="text-white/80 text-sm">{label}</div>
      {ok ? <StatusBadge tone="success" icon={Check}>Configured</StatusBadge> : <StatusBadge tone="neutral" icon={X}>Not configured</StatusBadge>}
    </div>
  );
}

export default function AdminCreem() {
  const { t, formatDate } = useLang();
  const [s, setS] = React.useState(null);
  const [form, setForm] = React.useState({});
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = () => api.get("/api/admin/creem/settings").then((r) => {
    setS(r.data);
    setForm({
      mode: r.data.mode,
      success_url: r.data.success_url || "",
      cancel_url: r.data.cancel_url || "",
      portal_enabled: r.data.portal_enabled,
      default_currency: r.data.default_currency,
      tax_mode: r.data.tax_mode,
      tax_category: r.data.tax_category,
      min_topup_usd: r.data.min_topup_usd,
      max_topup_usd: r.data.max_topup_usd,
      usd_to_credits: r.data.usd_to_credits,
      topup_bonus_tiers: JSON.stringify(r.data.topup_bonus_tiers || [], null, 2),
      test_api_key: "", live_api_key: "",
      webhook_secret_test: "", webhook_secret_live: "",
    });
  });
  React.useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let bonus = [];
      try { bonus = JSON.parse(form.topup_bonus_tiers || "[]"); } catch { toast.error("Bonus tiers is invalid JSON"); setBusy(false); return; }
      const payload = { ...form, topup_bonus_tiers: bonus,
        min_topup_usd: Number(form.min_topup_usd), max_topup_usd: Number(form.max_topup_usd), usd_to_credits: Number(form.usd_to_credits),
      };
      ["test_api_key", "live_api_key", "webhook_secret_test", "webhook_secret_live"].forEach((k) => {
        if (!payload[k] || !payload[k].trim()) delete payload[k];
      });
      await api.put("/api/admin/creem/settings", payload);
      toast.success(t("common.saved")); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };
  const testConn = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/api/admin/creem/test-connection");
      setTestResult(data);
      if (data.ok) toast.success("Creem OK"); else toast.error(data.error || "Failed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setTesting(false);
  };

  if (!s) return (<div className="space-y-6"><PageHeader title={t("admin.creem.title")} description={t("admin.creem.subtitle")} /><CardSkeleton h="h-96" /></div>);
  const backend = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
  const webhookUrl = `${backend}/api/creem/webhook`;

  return (
    <div className="space-y-6" data-testid="admin-creem-page">
      <PageHeader title={t("admin.creem.title")} description={t("admin.creem.subtitle")}
        badge={<StatusBadge tone={s.mode === "live" ? "warning" : "info"}>{s.mode}</StatusBadge>} />

      <SectionCard>
        <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.creem.mode")}</label>
            <div className="flex gap-2 flex-wrap">
              {["test", "live"].map((m) => (
                <button type="button" key={m} onClick={() => setForm({ ...form, mode: m })}
                  className={`px-4 py-2 rounded-full text-sm ${form.mode === m ? "bg-[#d4af37] text-[#0a0b0e]" : "border border-white/10 text-white/70"}`}
                  data-testid={`creem-mode-${m}`}>{t(`admin.creem.mode.${m}`)}</button>
              ))}
            </div>
          </div>
          {[
            ["test_api_key", "admin.creem.test_key", s.test_api_key_set ? s.test_api_key_mask : "creem_test_...", "creem-test-key"],
            ["live_api_key", "admin.creem.live_key", s.live_api_key_set ? s.live_api_key_mask : "creem_live_...", "creem-live-key"],
            ["webhook_secret_test", "admin.creem.webhook_test", s.webhook_secret_test_set ? s.webhook_secret_test_mask : "whsec_...", "creem-webhook-test"],
            ["webhook_secret_live", "admin.creem.webhook_live", s.webhook_secret_live_set ? s.webhook_secret_live_mask : "whsec_...", "creem-webhook-live"],
          ].map(([k, key, ph, tid]) => (
            <div key={k}>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t(key)}</label>
              <input className="lp-input" placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={tid} />
            </div>
          ))}
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.creem.success_url")}</label>
            <input className="lp-input" value={form.success_url} onChange={(e) => setForm({ ...form, success_url: e.target.value })} placeholder={`${backend}/payment/success`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.creem.cancel_url")}</label>
            <input className="lp-input" value={form.cancel_url} onChange={(e) => setForm({ ...form, cancel_url: e.target.value })} placeholder={`${backend}/payment/cancel`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.creem.webhook_url")}</label>
            <input className="lp-input font-mono text-xs" readOnly value={webhookUrl} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.creem.currency")}</label>
            <input className="lp-input" value={form.default_currency} onChange={(e) => setForm({ ...form, default_currency: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.creem.tax_mode")}</label>
            <select className="lp-input" value={form.tax_mode} onChange={(e) => setForm({ ...form, tax_mode: e.target.value })}>
              <option value="inclusive">inclusive</option><option value="exclusive">exclusive</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.creem.tax_category")}</label>
            <input className="lp-input" value={form.tax_category} onChange={(e) => setForm({ ...form, tax_category: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.creem.min_topup")}</label>
            <input type="number" step="0.01" min="0" className="lp-input tabular-nums" value={form.min_topup_usd} onChange={(e) => setForm({ ...form, min_topup_usd: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.creem.max_topup")}</label>
            <input type="number" step="0.01" min="0" className="lp-input tabular-nums" value={form.max_topup_usd} onChange={(e) => setForm({ ...form, max_topup_usd: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.creem.rate")}</label>
            <input type="number" min="1" className="lp-input tabular-nums" value={form.usd_to_credits} onChange={(e) => setForm({ ...form, usd_to_credits: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.creem.bonus")}</label>
            <textarea className="lp-input font-mono text-xs min-h-[100px]" value={form.topup_bonus_tiers} onChange={(e) => setForm({ ...form, topup_bonus_tiers: e.target.value })} />
          </div>
          <label className="md:col-span-2 flex items-center gap-3">
            <input type="checkbox" checked={!!form.portal_enabled} onChange={(e) => setForm({ ...form, portal_enabled: e.target.checked })} />
            <span className="text-white/70 text-sm">{t("admin.creem.portal")}</span>
          </label>
          <div className="md:col-span-2 flex gap-2 pt-2">
            <button type="submit" disabled={busy} className="lp-btn-primary" data-testid="creem-save"><Save className="w-4 h-4" /> {t("admin.creem.save")}</button>
            <button type="button" disabled={testing} onClick={testConn} className="lp-btn-secondary" data-testid="creem-test-conn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />} {t("admin.creem.test_conn")}
            </button>
          </div>
          {testResult && (
            <div className={`md:col-span-2 text-sm ${testResult.ok ? "text-emerald-400" : "text-red-400"}`}>
              {testResult.ok ? `OK · mode=${testResult.mode} · sample=${testResult.sample_count}` : testResult.error}
            </div>
          )}
          <div className="md:col-span-2 text-xs text-white/40">{t("admin.creem.masked_notice")}</div>
        </form>
      </SectionCard>

      <SectionCard title="Integration status">
        <StatusRow label={t("admin.creem.status.test")} ok={s.test_api_key_set} />
        <StatusRow label={t("admin.creem.status.live")} ok={s.live_api_key_set} />
        <StatusRow label={t("admin.creem.status.webhook_test")} ok={s.webhook_secret_test_set} />
        <StatusRow label={t("admin.creem.status.webhook_live")} ok={s.webhook_secret_live_set} />
        <div className="grid sm:grid-cols-2 gap-3 mt-4 text-xs text-white/60 font-mono">
          <div>{t("admin.creem.last_test")}: {s.last_test_success_at ? formatDate(s.last_test_success_at) : "—"}</div>
          <div>{t("admin.creem.last_webhook")}: {s.last_webhook_at ? formatDate(s.last_webhook_at) : "—"}</div>
          <div>{t("admin.creem.last_payment")}: {s.last_success_payment_at ? formatDate(s.last_success_payment_at) : "—"}</div>
          <div className="text-red-300">{t("admin.creem.last_error")}: {s.last_integration_error || "—"}</div>
        </div>
      </SectionCard>
    </div>
  );
}
